import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChildren } from '@angular/core';

import { Subscription } from 'rxjs';

import { VideogularComponent } from '@vbrick/vbrick-player/app/player/videogular/Videogular.Component';

import { HourMs } from 'rev-shared/date/Time.Constant';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { SignalRHubsConnection } from 'rev-shared/push/SignalRHubsConnection';
import { SignalRHubsConnectionToken } from 'rev-shared/push/SignalRHubsConnectionToken';

import { ApprovalStatusInfo } from './approvalProcess/ApprovalStatusInfo';
import { IVideoReviewData, VideoReviewTimeMarkerCSSClass } from './videoReview/VideoReviewTypes';
import { VideoReviewAlertEntryComponent } from './videoReview/VideoReviewAlertEntry.Component';
import { VideoReviewManualCommentEntryComponent } from './videoReview/VideoReviewManualCommentEntry.Component';
import { VideoReviewService } from './videoReview/VideoReview.Service';

import styles from './VideoReviewContainer.module.less';

@Component({
	selector: 'video-review-container',
	templateUrl: './VideoReviewContainer.Component.html',
	host: { '[class]': 'styles.root' }
})
export class VideoReviewContainerComponent implements OnInit, OnChanges, OnDestroy {
	@Input({ required: true }) public mediaFeatures: IMediaFeatures;
	@Input({ required: true }) public video: any;
	@Input({ required: true }) public vgApi: VideogularComponent;
	@ViewChildren('complianceAlerts') public complianceAlerts: QueryList<VideoReviewAlertEntryComponent>;
	@ViewChildren('manualComments') public manualComments: QueryList<VideoReviewManualCommentEntryComponent>;

	public readonly styles = styles;

	private unsubscribePush: IUnsubscribe;
	private timeMarkerClickedSub: Subscription;

	public isAlertsTabOpen: boolean = true;
	public statusInfo: ApprovalStatusInfo;

	constructor(
		@Inject(SignalRHubsConnectionToken) private readonly SignalRHubsConnection: SignalRHubsConnection,
		public VideoReviewService: VideoReviewService
	) { }

	public ngOnInit(): void {
		if (!this.SignalRHubsConnection.started) {
			this.SignalRHubsConnection.start()
				.catch(e => console.error('Error Starting SignalR: ', e));
		}

		this.VideoReviewService.getVideoReviewData(this.video.id).then((reviewData: IVideoReviewData) => {
			this.isAlertsTabOpen = !!reviewData.complianceAlerts?.length;
			this.unsubscribePush = this.VideoReviewService.subscribePush(this.video.id);
			this.VideoReviewService.setComplianceTabProcessingOverlay(this.statusInfo.isContentAnalysisJobPending && !reviewData.complianceAlerts?.length);
		});

		this.timeMarkerClickedSub = this.vgApi.vgTimeMarkerClicked.subscribe(marker => {
			this.isAlertsTabOpen = marker.cssClassName === VideoReviewTimeMarkerCSSClass.Alert;
			this.VideoReviewService.highlightContainerById(marker.id);

			setTimeout(() => {
				const markerElements = marker.cssClassName === VideoReviewTimeMarkerCSSClass.Alert ? this.complianceAlerts : this.manualComments;
				const el = markerElements.find(element => element.elementRef.nativeElement.id === marker.id)?.elementRef;
				el?.nativeElement.scrollIntoView({ behavior: 'smooth' });
			}, 0);
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.video && this.video) {
			this.statusInfo = new ApprovalStatusInfo(this.video.approval.status, this.video.approval.contentAnalysis);
			if (this.statusInfo) {
				this.VideoReviewService.setComplianceTabProcessingOverlay(this.statusInfo.isContentAnalysisJobPending && !this.VideoReviewService.reviewData.complianceAlerts?.length);
			}
		}
	}

	public ngOnDestroy(): void {
		this.VideoReviewService.clearServiceData();
		this.unsubscribePush?.();
		this.timeMarkerClickedSub?.unsubscribe();
	}

	public onAlertsTabClick(): void {
		this.isAlertsTabOpen = true;
	}

	public onCommentsTabClick(): void {
		this.isAlertsTabOpen = false;
	}

	public onTimeClicked(time: number): void {
		this.vgApi.seekTime(time);
	}

	private get durationMs(): number {
		if (this.vgApi) {
			return this.vgApi.totalTime;
		}
		return 0;
	}

	public get isShowHours(): boolean {
		return this.durationMs >= HourMs;
	}

}
